import { useRef, useEffect } from 'react'
import { CustomCmsSeller } from '../../../../domain/seller'
import { setInfoWindowContent } from '../helper'
import { BaiduSellersMapOptions } from './types'
import { baiduGetMapEl } from './helpers'

type CustomMarker = BMap.Marker & { id: number }

const useBaiduSellersMap = (sellers: CustomCmsSeller[], options: BaiduSellersMapOptions) => {
  const mapRef = useRef<BMap.Map>()
  const markerClustererRef = useRef<BMapLib.MarkerClusterer>()
  const markersRef = useRef<CustomMarker[]>()

  useEffect(() => {
    try {
      const mapEl = baiduGetMapEl()
      if (!mapEl) {
        console.warn('no ref for map found') // eslint-disable-line
        return () => {}
      }

      // init map
      if (!mapRef.current) {
        mapRef.current = new BMap.Map(mapEl)

        const point = new BMap.Point(121.43969947503291, 31.299555549886847)
        mapRef.current.centerAndZoom(point, 3)

        // Enable mouse wheel zoom control
        mapRef.current.enableScrollWheelZoom(true)

        // Add navigation control (Zoom in/out)
        const navigationControl = new BMap.NavigationControl({
          type: BMAP_NAVIGATION_CONTROL_LARGE,
        })
        mapRef.current.addControl(navigationControl)

        // Enable control to display satellite view
        const mapTypeControl = new BMap.MapTypeControl({
          mapTypes: [BMAP_NORMAL_MAP, BMAP_HYBRID_MAP],
        })
        mapRef.current.addControl(mapTypeControl)
        mapRef.current.addEventListener('zoomend', () => {
          if (options.onZoomEnd) {
            options.onZoomEnd(mapRef.current as BMap.Map)
          }
        })
      }

      const size = mapRef.current?.getSize() ?? { width: 0, height: 0 }

      const markers: CustomMarker[] = sellers.map((seller) => {
        const point = new BMap.Point(seller.longitude, seller.latitude)
        const marker = new BMap.Marker(point, {
          icon: new BMap.Icon(
            `/assets/img/map/marker/${seller.isMessika ? 'marker_01.png' : 'marker_02.png'}`,

            new BMap.Size(40, 60),
          ),
        }) as CustomMarker

        marker.id = seller.id

        marker.addEventListener('click', () => {
          options.onClickMarker?.(seller)
          const infoWindow = new BMap.InfoWindow(setInfoWindowContent(seller), {
            offset: new BMap.Size(0, -30),
            width: size?.width >= 300 ? 290 : size?.width - 150,
            maxWidth: 300,
          })
          mapRef.current?.openInfoWindow(infoWindow, marker.getPosition())
        })

        return marker
      })

      markersRef.current = markers

      markerClustererRef.current = new BMapLib.MarkerClusterer(mapRef.current, {
        markers,
        styles: [
          {
            url: '/assets/img/map/markerclusterer/m1.png',
            size: new BMap.Size(53, 53),
            textColor: '#fff',
            textSize: 12,
          },
        ],
      })

      return () => mapRef.current?.clearOverlays()
    } catch (e) {
      console.warn(e) // eslint-disable-line
    }
  }, [sellers])

  return { mapRef, markerClustererRef, markersRef }
}

export default useBaiduSellersMap
